import React, { useState } from 'react';
import { ChevronLeft, Sliders, CreditCard, Clock } from 'lucide-react';

interface VerticalVideoCreatorProps {
  onBack: () => void;
}

const VerticalVideoCreator: React.FC<VerticalVideoCreatorProps> = ({ onBack }) => {
  const [videoPrompt, setVideoPrompt] = useState<string>('');
  const [videoAspectRatio, setVideoAspectRatio] = useState<string>('9:16');
  const [videoDuration, setVideoDuration] = useState<number>(15);
  const [soundPrompt, setSoundPrompt] = useState<string>('');
  const [videoAI, setVideoAI] = useState<string>('Sora');
  
  // 利用可能な動画生成AI
  const videoAIOptions: string[] = ['Sora', 'Kling', 'Rundwa', 'Pika'];
  
  // クレジット計算
  const calculateCredits = (): number => {
    // 動画時間に基づくクレジット計算のロジック
    const baseCredits: number = 5;
    const durationFactor: number = Math.ceil(videoDuration / 5);
    return baseCredits * durationFactor;
  };
  
  // 生成時間計算
  const calculateGenerationTime = (): number => {
    // 動画時間に基づく生成時間の推定
    const baseTime: number = 30; // 秒
    const durationFactor: number = Math.ceil(videoDuration / 10);
    return baseTime * durationFactor;
  };
  
  // クレジット数の取得
  const requiredCredits: number = calculateCredits();
  
  // 推定生成時間の取得
  const estimatedTime: number = calculateGenerationTime();
  
  // 推定時間のフォーマット（秒を分:秒の形式に）
  const formatTime = (timeInSeconds: number): string => {
    const minutes: number = Math.floor(timeInSeconds / 60);
    const seconds: number = timeInSeconds % 60;
    return `${minutes}:${seconds < 10 ? '0' + seconds : seconds}`;
  };
  
  return (
    <div className="bg-gray-100">
      <div className="bg-white px-4 py-3 flex items-center shadow">
        <button 
          onClick={onBack}
          className="text-blue-500 mr-3"
        >
          <ChevronLeft size={24} />
        </button>
        <h2 className="text-lg font-bold">縦型ショート動画作成</h2>
      </div>
      
      <div className="p-4">
        <div className="mb-6">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-semibold">動画設定</h3>
            
            <div className="bg-gray-100 rounded-lg p-1">
              {videoAIOptions.map((ai) => (
                <button 
                  key={ai}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    videoAI === ai 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-transparent text-gray-600 hover:bg-gray-200'
                  }`}
                  onClick={() => setVideoAI(ai)}
                >
                  {ai}
                </button>
              ))}
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2 text-gray-700">動画プロンプト</label>
            <textarea
              className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all"
              placeholder="詳細なシーン説明を入力（例：夕日の海辺で波が打ち寄せる様子）"
              value={videoPrompt}
              onChange={(e) => setVideoPrompt(e.target.value)}
              rows={4}
            />
          </div>
          
          <div className="flex mb-4 space-x-4">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-2 text-gray-700">アスペクト比</label>
              <div className="grid grid-cols-4 gap-1 bg-gray-100 p-1 rounded-lg">
                {['9:16', '16:9', '1:1', '4:5'].map((ratio) => (
                  <button 
                    key={ratio}
                    className={`py-2 text-xs font-medium rounded ${
                      videoAspectRatio === ratio 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    onClick={() => setVideoAspectRatio(ratio)}
                  >
                    {ratio}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="flex-1">
              <label className="block text-sm font-medium mb-2 text-gray-700">動画の長さ</label>
              <div className="grid grid-cols-3 gap-1 bg-gray-100 p-1 rounded-lg">
                {[15, 30, 60].map((seconds) => (
                  <button 
                    key={seconds}
                    className={`py-2 text-xs font-medium rounded ${
                      videoDuration === seconds 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    onClick={() => setVideoDuration(seconds)}
                  >
                    {seconds}秒
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3">サウンド設定</h3>
          
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2 text-gray-700">音楽・サウンドプロンプト</label>
            <textarea
              className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all"
              placeholder="BGMやナレーションの説明（例：穏やかなピアノの曲、波の音を含む）"
              value={soundPrompt}
              onChange={(e) => setSoundPrompt(e.target.value)}
              rows={3}
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-lg p-4 shadow flex items-center">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
              <CreditCard size={20} className="text-blue-500" />
            </div>
            <div>
              <p className="text-xs text-gray-500">必要クレジット</p>
              <p className="font-semibold">{requiredCredits} クレジット</p>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-4 shadow flex items-center">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
              <Clock size={20} className="text-blue-500" />
            </div>
            <div>
              <p className="text-xs text-gray-500">推定生成時間</p>
              <p className="font-semibold">約 {formatTime(estimatedTime)}</p>
            </div>
          </div>
        </div>
        
        <button 
          className={`w-full py-3 rounded-lg font-medium text-white mb-4 ${
            !videoPrompt || !soundPrompt 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
          }`}
          disabled={!videoPrompt || !soundPrompt}
        >
          生成開始
        </button>
        
        <button className="w-full flex items-center justify-center py-2 text-blue-500 text-sm font-medium">
          詳細設定
          <Sliders size={14} className="ml-1" />
        </button>
      </div>
    </div>
  );
};

export default VerticalVideoCreator;